import { useEffect, useState } from "react";
import { Archive, Droplets, MapPin, Pencil, Plus, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", address: "", lat: "", lng: "", contact_name: "", contact_phone: "", power_available: false, water_available: false, max_vehicle_height: "", site_rules: "", image_url: "" };

export default function Venues() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/venues").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...v, lat: v.lat ?? "", lng: v.lng ?? "", max_vehicle_height: v.max_vehicle_height ?? "", site_rules: (v.site_rules || []).join(", ") });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      max_vehicle_height: form.max_vehicle_height === "" ? null : Number(form.max_vehicle_height),
      site_rules: form.site_rules.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.patch(`/venues/${editing.id}`, payload);
      else await api.post("/venues", payload);
      toast.success(editing ? "Venue updated" : "Venue added");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const archive = async (v) => {
    try {
      await api.patch(`/venues/${v.id}`, { archived: !v.archived });
      toast.success(v.archived ? "Venue restored" : "Venue archived");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="venues-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Venues</h1>
          <p className="mt-1 text-sm text-muted-foreground">Partner locations, trading sites and private estates with site rules.</p>
        </div>
        <Button data-testid="new-venue-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Add Venue
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((v) => (
          <div key={v.id} data-testid={`venue-card-${v.id}`} className={`border border-border bg-white transition-transform duration-200 hover:-translate-y-[2px] ${v.archived ? "opacity-50" : ""}`}>
            {v.image_url && (
              <div className="h-36 w-full overflow-hidden border-b border-border">
                <img src={v.image_url} alt={v.name} className="h-full w-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <h3 className="font-heading text-base font-bold tracking-tight">{v.name}</h3>
                {v.archived && <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Archived</span>}
              </div>
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {v.address || "No address"}</p>
              {v.lat != null && v.lng != null && (
                <p className="mt-1 font-mono text-[11px] text-muted-foreground" data-testid={`venue-coords-${v.id}`}>{Number(v.lat).toFixed(4)}, {Number(v.lng).toFixed(4)}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {v.power_available && <span className="flex items-center gap-1 border border-emerald-600 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><Zap className="h-3 w-3" /> Power</span>}
                {v.water_available && <span className="flex items-center gap-1 border border-blue-600 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700"><Droplets className="h-3 w-3" /> Water</span>}
                {v.max_vehicle_height && <span className="border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Max {v.max_vehicle_height}m</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(v.site_rules || []).map((r) => (
                  <span key={r} className="bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground" data-testid={`venue-rule-${v.id}-${r.slice(0, 8)}`}>{r}</span>
                ))}
              </div>
              <div className="mt-4 flex gap-2 border-t border-border pt-4">
                <Button data-testid={`edit-venue-${v.id}`} variant="outline" size="sm" onClick={() => openEdit(v)} className="rounded-md"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`archive-venue-${v.id}`} variant="outline" size="sm" onClick={() => archive(v)} className="rounded-md text-muted-foreground"><Archive className="mr-1 h-3.5 w-3.5" /> {v.archived ? "Restore" : "Archive"}</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="venue-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Venue" : "Add Venue"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Name</Label><Input data-testid="venue-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input data-testid="venue-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Latitude</Label><Input data-testid="venue-lat-input" type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Longitude</Label><Input data-testid="venue-lng-input" type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Site contact</Label><Input data-testid="venue-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact phone</Label><Input data-testid="venue-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            <div className="flex items-center justify-between border border-border px-3 py-2">
              <Label className="text-sm">Power available</Label>
              <Switch data-testid="venue-power-switch" checked={form.power_available} onCheckedChange={(v) => setForm({ ...form, power_available: v })} />
            </div>
            <div className="flex items-center justify-between border border-border px-3 py-2">
              <Label className="text-sm">Water available</Label>
              <Switch data-testid="venue-water-switch" checked={form.water_available} onCheckedChange={(v) => setForm({ ...form, water_available: v })} />
            </div>
            <div className="space-y-1.5"><Label>Max vehicle height (m)</Label><Input data-testid="venue-height-input" type="number" step="0.1" value={form.max_vehicle_height} onChange={(e) => setForm({ ...form, max_vehicle_height: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Image URL</Label><Input data-testid="venue-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Site rules (comma separated)</Label><Input data-testid="venue-rules-input" placeholder="No generators after 10 PM, Max vehicle height 3.2m" value={form.site_rules} onChange={(e) => setForm({ ...form, site_rules: e.target.value })} /></div>
            <div className="col-span-2"><Button data-testid="venue-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Add Venue"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
